import { useSectorEnricher } from "@/Domains/Country";
import { NeedModel } from "../NeedModel";
import { useContact} from '@/OneLiners';
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";


/**
 * =====================================================
 * Hook: useNeedEnricher
 * =====================================================
 *
 * Description:
 * Custom hook responsible for enriching Need domain
 * models with related cross-domain data.
 *
 * Aggregates and resolves:
 * - users (audit & ownership)
 * - contacts
 * - price ranges
 * - sectors and their related cities
 *
 * Provides a unified enrichment API usable by
 * both list and detail views.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useNeedEnricher() {

    /**
     * =====================================================
     * Related Domain Data Fetching
     * =====================================================
     *
     * Each hook provides:
     * - dataMap: domain entities
     * - stateStatus: loading lifecycle state
     * - stateError: potential error
     */

    const { 
        stateResponse: contactResponse, 
        dataMap: contactMap 
    } = useContact();

    
    const {sectorMap, cityMap, countryMap, response : sectorEnricherResponse} = useSectorEnricher();

    /**
         * Centralized audit enrichment hook
         * - Provides O(1) audit user resolution via precomputed Map
         * - Exposes audit status and error
         */
        const {
            enricherResponse : auditEnricherResponse,
            userMap,
            getUserById,
            getEnrichedAudit,
        } = useAuditEnricher<NeedModel>();


    /**
     * =====================================================
     * Combined Loading & Error State
     * =====================================================
     *
     * Aggregates all related domain states into
     * a single ActionResponse object.
     */
    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            contactResponse.status,
            auditEnricherResponse.status,
            sectorEnricherResponse.status),

        error:
            contactResponse.error ??
            auditEnricherResponse.error ??
            sectorEnricherResponse.error
    };


    /**
     * =====================================================
     * Public Enrichment Helpers
     * =====================================================
     */

    const getEnrichedNeedMap = (needMap: RecordMap<NeedModel>): RecordMap<NeedModel> => {
            
            return needMap.map<NeedModel>(getStrictEnrichedNeed);
        }

    const getEnrichedNeed = (need?: NeedModel) : NeedModel | undefined => {
        if(!need) return undefined;
        return getStrictEnrichedNeed(need);
    }


    /**
     * Enriches a single Need model.
     *
     * Resolves:
     * - audit fields
     * - related contact
     * - related price range
     * - sector and its associated city
     */
    const getStrictEnrichedNeed = (need: NeedModel) => {
        const sector = need.sectorId ? sectorMap.get(need.sectorId) : undefined;
        const city = sector?.cityId ? cityMap.get(sector.cityId) : undefined;
        
        return {
            ...getEnrichedAudit(need),
            contact: need.contactId ? contactMap.get(need.contactId) : undefined,
            sector: sector ? { ...sector, city } : undefined,
        }
    }

     /**
     * Enriches a Need model by its identifier.
     */
    const getEnrichedNeedById = (needMap: Map<number, NeedModel>,
        needId?: number | null
    ) : NeedModel | undefined => {
        return getEnrichedNeed(needId ? needMap.get(needId) : undefined) ;
    }

    /**
     * =====================================================
     * Return API
     * =====================================================
     *
     * Exposes enriched data, related lists,
     * and helper functions for consumers.
     */
    return {
        userMap,
        sectorMap,
        cityMap,
        countryMap,
        contactMap,
        enricherResponse,
        getEnrichedNeed,
        getStrictEnrichedNeed,
        getEnrichedNeedById,
        getEnrichedNeedMap
    }
}